import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { Textarea } from "@loot-os/ui/components/textarea";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useId, useState } from "react";
import { type CatalogDraftRow, emptyCatalogRow } from "../lib/dropTableDraft";
import {
  IMPORT_ISSUE_MESSAGES,
  importedWeightAsPercent,
  lookupIds,
  parseDropTableImport,
  type WeightUnit,
} from "../lib/dropTableImport";
import { trpcClient } from "../lib/trpc";

export type ImportMode = "append" | "replace";

const MODE_LABELS: Record<ImportMode, string> = {
  append: "Add to the table",
  replace: "Replace the table",
};

const UNIT_LABELS: Record<"auto" | WeightUnit, string> = {
  auto: "Work it out",
  percent: "Percent (10 = 10%)",
  ppm: "Parts per million (100000 = 10%)",
};

/**
 * Pastes a drop table in from a sheet: one row per line, the catalog item id (boxed's asset
 * group id, the sheet's "Retool ID") then the weight, separated by a tab or a comma. A CSV file
 * reads the same way. Every id is looked up in one request; an id the box may not name (a typo,
 * another tenant's item, an item a box template cannot share) comes in as a row to re-pick, with
 * the pasted id shown on it, so nothing pasted is silently dropped.
 */
export function DropTableImportDialog({
  open,
  onOpenChange,
  sharedOnly,
  onImport,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Box templates may only name shared catalog items. */
  sharedOnly: boolean;
  onImport: (rows: CatalogDraftRow[], mode: ImportMode) => void;
}) {
  const id = useId();
  const [text, setText] = useState("");
  const [mode, setMode] = useState<ImportMode>("append");
  const [unit, setUnit] = useState<"auto" | WeightUnit>("auto");
  const [pending, setPending] = useState(false);
  const parsed = parseDropTableImport(text, unit === "auto" ? undefined : unit);

  const reset = () => {
    setText("");
    setUnit("auto");
    setMode("append");
  };

  const readFile = async (file: File | undefined) => {
    if (!file) return;
    setText(await file.text());
  };

  const submit = async () => {
    if (parsed.unit === null || parsed.rows.length === 0) return;
    setPending(true);
    try {
      const ids = lookupIds(parsed.rows);
      const found = ids.length
        ? sharedOnly
          ? await trpcClient.platform.boxTemplates.catalogByIds.query({ ids })
          : await trpcClient.fulfillment.catalog.byIds.query({ ids })
        : { items: [] };
      const byId = new Map(found.items.map((item) => [item.id.toLowerCase(), item]));
      const weightUnit = parsed.unit;
      let unresolved = 0;
      const rows = parsed.rows.map((row): CatalogDraftRow => {
        const weight = importedWeightAsPercent(row.weight, weightUnit);
        const item = byId.get(row.id.toLowerCase());
        if (!item || item.value === null) {
          unresolved++;
          // No catalog id, and the pasted id as the name: the picker asks to replace it.
          return { ...emptyCatalogRow(), name: row.id, weight };
        }
        return {
          ...emptyCatalogRow(),
          catalogItemId: item.id,
          name: item.name,
          imageUrl: item.overrideImage ?? item.image ?? "",
          value: item.value,
          marketPriceCents: item.marketPrice ?? null,
          weight,
        };
      });
      onImport(rows, mode);
      toast.success(
        unresolved === 0
          ? `Imported ${formatNumber(rows.length)} rows`
          : `Imported ${formatNumber(rows.length)} rows; ${formatNumber(unresolved)} need re-picking`,
      );
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "import failed");
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import rows</DialogTitle>
          <DialogDescription>
            Paste from a sheet, one row per line: the catalog item id (the Retool asset group id),
            then the weight. Tabs or commas both work, and a header row is skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor={`${id}-text`}>Rows</Label>
            <Textarea
              id={`${id}-text`}
              className="h-48 font-mono text-xs"
              placeholder={"b182bd5a-5fb3-565f-9ef5-d5edf7630bf7\t12.5\n0f0e7c1e-…\t0.25%"}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <input
              type="file"
              accept=".csv,.tsv,.txt,text/csv,text/plain"
              className="text-xs text-muted-foreground file:mr-2 file:rounded file:border file:bg-transparent file:px-2 file:py-1"
              onChange={(e) => void readFile(e.target.files?.[0])}
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <div className="grid gap-2">
              <Label>Rows go</Label>
              <Select
                value={mode}
                items={MODE_LABELS}
                onValueChange={(value) => setMode(value as ImportMode)}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(MODE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Weights are in</Label>
              <Select
                value={unit}
                items={UNIT_LABELS}
                onValueChange={(value) => setUnit(value as "auto" | WeightUnit)}
              >
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(UNIT_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {text.trim() !== "" && (
            <div className="grid gap-1 text-sm">
              <p>
                {formatNumber(parsed.rows.length)} {parsed.rows.length === 1 ? "row" : "rows"}
                {parsed.unit
                  ? `, weights read as ${parsed.unit === "percent" ? "percent" : "parts per million"}`
                  : ""}
                .
              </p>
              {parsed.unit === null && (
                <p className="text-amber-700 dark:text-amber-400">
                  The weights total neither 100 nor 1,000,000, so say which unit they are in.
                </p>
              )}
              {parsed.issues.length > 0 && (
                <ul className="max-h-32 overflow-auto text-xs text-muted-foreground">
                  {parsed.issues.map((issue) => (
                    <li key={`${issue.line}-${issue.reason}`}>
                      Line {issue.line} skipped: {IMPORT_ISSUE_MESSAGES[issue.reason]} —{" "}
                      <span className="font-mono">{issue.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void submit()}
            disabled={pending || parsed.rows.length === 0 || parsed.unit === null}
          >
            {pending ? "Importing…" : `Import ${formatNumber(parsed.rows.length)} rows`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
